<template>
  <span
    :class="[
      $style.status,
      status === 'draft' && $style.draft,
      status === 'changed' && $style.changed,
      status === 'published' && $style.published,
      status === 'archived' && $style.archived,
    ]"
  >
    <slot>{{ status }}</slot>
  </span>
</template>

<script setup lang="ts">
defineProps<{
  status: 'draft' | 'changed' | 'published' | 'archived'
}>()
</script>

<style lang="scss" module>
.status {
  /* layout */
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;

  /* type */
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    'Open Sans',
    'Helvetica Neue',
    sans-serif;
  font-size: 11px;
  line-height: 1.6;
  font-weight: bold;
  letter-spacing: 0.02em;
  text-transform: uppercase;

  /* theming via CSS vars */
  --tag-bg: #eef2f6;
  --tag-fg: #1f2a37;
  --tag-border: #d4dee7;
  --tag-dot: currentColor;

  background: var(--tag-bg);
  color: var(--tag-fg);
  border: 1px solid var(--tag-border);

  /* subtle polish */
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  /* status dot */
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--tag-dot);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--tag-bg) 70%, transparent);
  }

  /* focus (keyboard) */
  &:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--tag-fg) 30%, transparent);
    outline-offset: 2px;
  }
}

.draft {
  --tag-bg: #fff7e6;
  --tag-fg: #7a4d00;
  --tag-border: #ffe3b3;
  --tag-dot: #f4a100;
}

.changed {
  --tag-bg: #ceecff;
  --tag-fg: #0059c8;
  --tag-border: #b3e0ff;
  --tag-dot: #0071fa;
}

.published {
  --tag-bg: #e9f8ef;
  --tag-fg: #0e7a3e;
  --tag-border: #cceedd;
  --tag-dot: #22a06b;
}

.archived {
  --tag-bg: #eef1f5;
  --tag-fg: #5b6b7a;
  --tag-border: #d9e0e7;
  --tag-dot: #8a95a3;
}
</style>
